import React from 'react'
import { Icon } from './icons'
import { DataState } from './shared'
import { fmtRelTime } from './format'
import type { ApiError, Archive, BackupsList, Capabilities, Role, Server } from './types'

interface BackupsPageProps {
  role: Role
  capabilities: Capabilities
  servers: Server[]
  data: BackupsList
  loading: boolean
  error: ApiError | null
  onRetry: () => void
  onTrigger: (serverId: string, note: string | null) => Promise<boolean>
  onRestore: (serverId: string, archive: string, confirm: string) => Promise<boolean>
}

function fmtSize(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${bytes} B`
}

export function BackupsPage({ role, capabilities, servers, data, loading, error, onRetry, onTrigger, onRestore }: BackupsPageProps) {
  const canManage = capabilities.canManageBackups
  const canRestore = capabilities.canRestoreBackup
  const [triggerOpen, setTriggerOpen] = React.useState(false)
  const [triggerServer, setTriggerServer] = React.useState(servers[0]?.id ?? '')
  const [note, setNote] = React.useState('')
  const [restoreTarget, setRestoreTarget] = React.useState<Archive | null>(null)
  const [confirmValue, setConfirmValue] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (!triggerServer && servers[0]) setTriggerServer(servers[0].id)
  }, [servers, triggerServer])

  if (role === 'player') {
    return (
      <>
        <div className="page-header" data-screen-label="Backups"><h1>Backups</h1></div>
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <Icon name="shield" size={26} style={{ opacity: 0.5, marginBottom: 10 }} />
          <h3 style={{ margin: '0 0 4px' }}>403 — Admin only</h3>
          <div className="dim">Players cannot view backups.</div>
        </div>
      </>
    )
  }

  async function handleTrigger() {
    if (!triggerServer) return
    setSubmitting(true)
    try {
      const ok = await onTrigger(triggerServer, note.trim() || null)
      if (ok) {
        setTriggerOpen(false)
        setNote('')
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function handleRestore() {
    if (!restoreTarget) return
    if (confirmValue !== restoreTarget.serverId) return
    setSubmitting(true)
    try {
      const ok = await onRestore(restoreTarget.serverId, restoreTarget.filename, confirmValue)
      if (ok) {
        setRestoreTarget(null)
        setConfirmValue('')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <div className="page-header" data-screen-label="Backups">
        <h1>Backups</h1>
        <span className="subtitle">— world archives under <span className="mono">/opt/mc/backups</span></span>
        <div className="header-right">
          {canManage && (
            <button className="btn primary" onClick={() => setTriggerOpen(open => !open)}>
              <Icon name="download" size={12} /> Create backup
            </button>
          )}
        </div>
      </div>

      {data.freeDiskGb > 0 && data.freeDiskGb < 10 && (
        <div className="banner warn" style={{ marginBottom: 14 }}>
          <Icon name="alert-triangle" size={14} />
          <div>
            <div style={{ fontWeight: 500 }}>Only {data.freeDiskGb} GB free on the backup disk.</div>
            <div className="text-xs" style={{ opacity: 0.85 }}>Free space before triggering more backups.</div>
          </div>
        </div>
      )}

      <div className="banner" style={{ marginBottom: 14 }}>
        <Icon name="shield" size={14} style={{ marginTop: 1 }} />
        <div>
          <div style={{ fontWeight: 500 }}>Backups are stored on the same VPS.</div>
          <div className="text-xs" style={{ opacity: 0.8, marginTop: 2 }}>Off-VPS backup target is not yet configured. Losing the disk = losing the archives.</div>
        </div>
      </div>

      {triggerOpen && canManage && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div className="flex gap-2 items-end" style={{ flexWrap: 'wrap' }}>
            <label style={{ display: 'block' }}>
              <div className="text-xs dim mb-1">Server</div>
              <div className="seg-radio">
                {servers.map(server => (
                  <button key={server.id} className={triggerServer === server.id ? 'active' : ''} onClick={() => setTriggerServer(server.id)}>{server.name}</button>
                ))}
              </div>
            </label>
            <label style={{ display: 'block', flex: '1 1 200px' }}>
              <div className="text-xs dim mb-1">Note (optional)</div>
              <input type="text" value={note} maxLength={200} onChange={event => setNote(event.target.value)} placeholder="e.g. pre-v1.21-upgrade" style={{ width: '100%' }} />
            </label>
            <div className="flex gap-2">
              <button className="btn ghost sm" onClick={() => { setTriggerOpen(false); setNote('') }}>Cancel</button>
              <button className="btn primary sm" disabled={submitting || !triggerServer} onClick={() => void handleTrigger()}>
                <Icon name="check" size={11} /> Run backup.sh
              </button>
            </div>
          </div>
          <div className="text-xs dim" style={{ marginTop: 6 }}>The server keeps running during the backup; takes the ops lock so lifecycle buttons are disabled until finished.</div>
        </div>
      )}

      <DataState loading={loading} error={error} onRetry={onRetry}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {data.archives.length === 0 ? (
            <div className="dim text-sm" style={{ padding: 28, textAlign: 'center' }}>No archives yet.</div>
          ) : (
            <div className="table-scroll">
              <table className="data-table reflow">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Server</th>
                    <th>Size</th>
                    <th>Age</th>
                    {canRestore && <th style={{ width: 130 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {data.archives.map(archive => (
                    <tr key={archive.filename}>
                      <td data-label="Filename" className="mono text-xs" style={{ fontWeight: 500 }}>{archive.filename}</td>
                      <td data-label="Server">{archive.serverId}</td>
                      <td data-label="Size" className="mono text-xs">{fmtSize(archive.sizeBytes)}</td>
                      <td data-label="Age" className="text-xs">{fmtRelTime(archive.modifiedAt)}</td>
                      {canRestore && (
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn sm ghost" onClick={() => { setRestoreTarget(archive); setConfirmValue('') }} style={{ color: 'var(--warn)' }}>
                            <Icon name="rotate-ccw" size={11} /> Restore
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DataState>

      {restoreTarget && (
        <div className="modal-overlay" onClick={() => setRestoreTarget(null)}>
          <div className="modal" onClick={event => event.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Restore {restoreTarget.filename}</h3>
            <div className="banner err" style={{ marginBottom: 10 }}>
              <Icon name="alert-triangle" size={14} />
              <div>
                <div style={{ fontWeight: 500 }}>The server must be stopped first.</div>
                <div className="text-xs" style={{ opacity: 0.85 }}>
                  The current world will be renamed to <span className="mono">world.before-restore-&lt;ts&gt;</span>. Start the server manually after restore.
                </div>
              </div>
            </div>
            <label style={{ display: 'block', marginBottom: 10 }}>
              <div className="text-xs dim mb-1">
                Type <span className="mono">{restoreTarget.serverId}</span> to confirm:
              </div>
              <input type="text" value={confirmValue} onChange={event => setConfirmValue(event.target.value)} style={{ width: '100%' }} />
            </label>
            <div className="flex gap-2 justify-end">
              <button className="btn ghost sm" onClick={() => setRestoreTarget(null)}>Cancel</button>
              <button
                className="btn danger sm"
                disabled={submitting || confirmValue !== restoreTarget.serverId}
                onClick={() => void handleRestore()}
              >
                <Icon name="rotate-ccw" size={11} /> Restore
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
